import { cls } from '@/ui/cls'
import ColumnFilter, { type SortDir } from './ColumnFilter'
import { type ColumnSort, type Filters, type SortKey, SORT_NUMERIC } from './columns'
import type { TableValues } from './useTableState'

interface Props {
  /** Sorting and filters wait for the full list. */
  inert?: boolean
  sorts: ColumnSort[]
  sortByColumn: (k: SortKey, numeric: boolean) => void
  sortFor: (k: SortKey) => SortDir
  sortRank: (k: SortKey) => number
  onSortChange: (k: SortKey) => (dir: SortDir) => void
  filters: Filters
  setFilter: <K extends keyof Filters>(key: K, value: Filters[K]) => void
  values: TableValues
}

export default function TableHead({
  inert, sorts, sortByColumn, sortFor, sortRank, onSortChange, filters, setFilter, values,
}: Props) {
  return (
    <thead inert={inert}>
      <tr>
        <th className="cell-num" title="Position i aktuell sortering">#</th>
        <Head label="Poäng" sortKey="score" sorts={sorts} onSort={sortByColumn} className="num">
          <ColumnFilter
            label="Poäng" mode="multi" numeric
            sort={sortFor('score')} sortRank={sortRank('score')} onSort={onSortChange('score')}
            values={values.score}
            range={filters.scoreRange}
            onRangeChange={r => setFilter('scoreRange', r)}
            selected={filters.scoreBucket}
            onSelectedChange={s => setFilter('scoreBucket', s)}
          />
        </Head>
        <Head label="Underlag" sortKey="underlag" sorts={sorts} onSort={sortByColumn} className="num" />
        <Head label="Företag" sortKey="company" sorts={sorts} onSort={sortByColumn}>
          <ColumnFilter
            label="Företag" mode="text"
            sort={sortFor('company')} sortRank={sortRank('company')} onSort={onSortChange('company')}
            text={filters.company}
            onTextChange={v => setFilter('company', v)}
          />
        </Head>
        <Head label="Län" sortKey="region" sorts={sorts} onSort={sortByColumn}>
          <ColumnFilter
            label="Län" mode="multi"
            sort={sortFor('region')} sortRank={sortRank('region')} onSort={onSortChange('region')}
            values={values.region}
            selected={filters.region}
            onSelectedChange={s => setFilter('region', s)}
          />
        </Head>
        <Head label="Ort" sortKey="city" sorts={sorts} onSort={sortByColumn}>
          <ColumnFilter
            label="Ort" mode="multi"
            sort={sortFor('city')} sortRank={sortRank('city')} onSort={onSortChange('city')}
            values={values.city}
            selected={filters.city}
            onSelectedChange={s => setFilter('city', s)}
          />
        </Head>
        <Head label="Kontakt" sortKey="contact" sorts={sorts} onSort={sortByColumn}>
          <ColumnFilter
            label="Kontakt" mode="text"
            sort={sortFor('contact')} sortRank={sortRank('contact')} onSort={onSortChange('contact')}
            text={filters.contact}
            onTextChange={v => setFilter('contact', v)}
          />
        </Head>
        <Head label="Telefon" sortKey="phone" sorts={sorts} onSort={sortByColumn}>
          <ColumnFilter
            label="Telefon" mode="multi"
            sort={sortFor('phone')} sortRank={sortRank('phone')} onSort={onSortChange('phone')}
            values={values.phone}
            selected={filters.hasPhone}
            onSelectedChange={s => setFilter('hasPhone', s)}
          />
        </Head>
        <Head label="E-post" sortKey="email" sorts={sorts} onSort={sortByColumn}>
          <ColumnFilter
            label="E-post" mode="multi"
            sort={sortFor('email')} sortRank={sortRank('email')} onSort={onSortChange('email')}
            values={values.email}
            selected={filters.emailStatus}
            onSelectedChange={s => setFilter('emailStatus', s)}
          />
        </Head>
        <Head label="Samtal 1–4" sortKey="callCount" sorts={sorts} onSort={sortByColumn} className="att-h" colSpan={4}>
          <ColumnFilter
            label="Samtalsförsök" mode="multi" numeric
            sort={sortFor('callCount')} sortRank={sortRank('callCount')} onSort={onSortChange('callCount')}
            values={values.callCount}
            range={filters.callCountRange}
            onRangeChange={r => setFilter('callCountRange', r)}
            selected={filters.callCount}
            onSelectedChange={s => setFilter('callCount', s)}
          />
        </Head>
        <Head label="Fick kontakt" sortKey="gotContact" sorts={sorts} onSort={sortByColumn}>
          <ColumnFilter
            label="Fick kontakt" mode="multi"
            sort={sortFor('gotContact')} sortRank={sortRank('gotContact')} onSort={onSortChange('gotContact')}
            values={values.gotContact}
            selected={filters.gotContact}
            onSelectedChange={s => setFilter('gotContact', s)}
          />
        </Head>
        <Head label="Betyg" sortKey="rating" sorts={sorts} onSort={sortByColumn}>
          <ColumnFilter
            label="Betyg" mode="multi" numeric
            sort={sortFor('rating')} sortRank={sortRank('rating')} onSort={onSortChange('rating')}
            values={values.rating}
            range={filters.ratingRange}
            onRangeChange={r => setFilter('ratingRange', r)}
            selected={filters.rating}
            onSelectedChange={s => setFilter('rating', s)}
          />
        </Head>
        <Head label="Stadie" sortKey="stage" sorts={sorts} onSort={sortByColumn}>
          <ColumnFilter
            label="Stadie" mode="multi"
            sort={sortFor('stage')} sortRank={sortRank('stage')} onSort={onSortChange('stage')}
            values={values.stage}
            selected={filters.stage}
            onSelectedChange={s => setFilter('stage', s)}
          />
        </Head>
        <Head label="Bokad" sortKey="booked" sorts={sorts} onSort={sortByColumn}>
          <ColumnFilter
            label="Bokad" mode="multi"
            sort={sortFor('booked')} sortRank={sortRank('booked')} onSort={onSortChange('booked')}
            values={values.booked}
            selected={filters.booked}
            onSelectedChange={s => setFilter('booked', s)}
          />
        </Head>
        <Head label="Trolig" sortKey="likely" sorts={sorts} onSort={sortByColumn}>
          <ColumnFilter
            label="Sannolikt bok" mode="multi"
            sort={sortFor('likely')} sortRank={sortRank('likely')} onSort={onSortChange('likely')}
            values={values.likely}
            selected={filters.likely}
            onSelectedChange={s => setFilter('likely', s)}
          />
        </Head>
        <Head label="Leaddatum" sortKey="leadDate" sorts={sorts} onSort={sortByColumn}>
          <ColumnFilter
            label="Leaddatum" mode="multi"
            sort={sortFor('leadDate')} sortRank={sortRank('leadDate')} onSort={onSortChange('leadDate')}
            values={values.leadDate}
            dateRange={filters.leadDateRange}
            onDateRangeChange={r => setFilter('leadDateRange', r)}
            selected={filters.leadDate}
            onSelectedChange={s => setFilter('leadDate', s)}
          />
        </Head>
        <Head label="Flyttdatum" sortKey="moveDate" sorts={sorts} onSort={sortByColumn}>
          <ColumnFilter
            label="Flyttdatum" mode="multi"
            sort={sortFor('moveDate')} sortRank={sortRank('moveDate')} onSort={onSortChange('moveDate')}
            values={values.moveDate}
            dateRange={filters.moveDateRange}
            onDateRangeChange={r => setFilter('moveDateRange', r)}
            selected={filters.moveDate}
            onSelectedChange={s => setFilter('moveDate', s)}
          />
        </Head>
        <Head label="Mötesdatum" sortKey="meetingDate" sorts={sorts} onSort={sortByColumn}>
          <ColumnFilter
            label="Mötesdatum" mode="multi"
            sort={sortFor('meetingDate')} sortRank={sortRank('meetingDate')} onSort={onSortChange('meetingDate')}
            values={values.meetingDate}
            dateRange={filters.meetingDateRange}
            onDateRangeChange={r => setFilter('meetingDateRange', r)}
            selected={filters.meetingDate}
            onSelectedChange={s => setFilter('meetingDate', s)}
          />
        </Head>
        <Head label="Ägare" sortKey="owner" sorts={sorts} onSort={sortByColumn}>
          <ColumnFilter
            label="Ägare" mode="multi"
            sort={sortFor('owner')} sortRank={sortRank('owner')} onSort={onSortChange('owner')}
            values={values.owner}
            selected={filters.owner}
            onSelectedChange={s => setFilter('owner', s)}
          />
        </Head>
        <Head label="Säljare" sortKey="seller" sorts={sorts} onSort={sortByColumn}>
          <ColumnFilter
            label="Säljare" mode="multi"
            sort={sortFor('seller')} sortRank={sortRank('seller')} onSort={onSortChange('seller')}
            values={values.seller}
            selected={filters.seller}
            onSelectedChange={s => setFilter('seller', s)}
          />
        </Head>
        <Head label="SF-status" sortKey="sfStatus" sorts={sorts} onSort={sortByColumn}>
          <ColumnFilter
            label="Salesforce-status" mode="multi"
            sort={sortFor('sfStatus')} sortRank={sortRank('sfStatus')} onSort={onSortChange('sfStatus')}
            values={values.sfStatus}
            selected={filters.sfStatus}
            onSelectedChange={s => setFilter('sfStatus', s)}
          />
        </Head>
        <Head label="Org.nr" sortKey="orgNo" sorts={sorts} onSort={sortByColumn}>
          <ColumnFilter
            label="Org.nr" mode="text"
            sort={sortFor('orgNo')} sortRank={sortRank('orgNo')} onSort={onSortChange('orgNo')}
            text={filters.orgNo}
            onTextChange={v => setFilter('orgNo', v)}
          />
        </Head>
      </tr>
    </thead>
  )
}

// Header cell — clickable label triggers sort, filter button opens the
// column's filter dropdown. Sort indicator (↑/↓) renders inline next to
// the label when this column is the active sort.
function Head({
  label, sortKey, sorts, onSort, className, colSpan, children,
}: {
  label: string
  sortKey: SortKey
  sorts: ColumnSort[]
  onSort: (k: SortKey, numeric: boolean) => void
  className?: string
  colSpan?: number
  children?: React.ReactNode
}) {
  const rank = sorts.findIndex(x => x.key === sortKey)
  const active = rank >= 0
  const dir = active ? sorts[rank].dir : null
  const numeric = SORT_NUMERIC[sortKey]
  return (
    <th className={className} colSpan={colSpan}>
      <div className="th-inner">
        <button
          type="button"
          className={cls('th-label', active && 'th-label-active')}
          onClick={() => onSort(sortKey, numeric)}
          title={
            active && sorts.length > 1
              ? `Sorterad efter ${label} (${rank + 1} av ${sorts.length})`
              : `Sortera efter ${label}`
          }
        >
          <span>{label}</span>
          {active && (
            <span className="th-sort-arrow">
              {dir === 'asc' ? '↑' : '↓'}
              {/* Only worth showing once the sort is a chain — a lone "1"
                  next to the only sorted column is noise. */}
              {sorts.length > 1 && (
                <span className="ml-px text-[9px] align-super tabular-nums">{rank + 1}</span>
              )}
            </span>
          )}
        </button>
        {children}
      </div>
    </th>
  )
}
